import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"

import * as Yup from "yup"
import type Contact from "~/types/Contact"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceAccounts } from "~/routes"

const contactSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Must be a valid email").required("Email is required"),
    phone: Yup.string(),
    role: Yup.string(),
    contactCategoryId: Yup.string().required("Category is required"),
    accountId: Yup.string(),
})

interface ContactFormProps {
    onSubmit: (values: any) => void
    initialValues?: any
    isEditing?: boolean
    contactCategories: Array<{ id: string; name: string }>
    initialAccount?: { id: string; name: string } | null
    isModal?: boolean
    isSubmitting?: boolean
}

const ContactForm: React.FC<ContactFormProps> = ({
                                                     onSubmit,
                                                     initialValues,
                                                     isEditing = false,
                                                     contactCategories,
                                                     initialAccount = null,
                                                     isModal = false,
                                                     isSubmitting = false,
                                                 }) => {
    const { venueSlug } = getAppConfig()
    const [accountSearch, setAccountSearch] = useState("")
    const [accountResults, setAccountResults] = useState<Array<{ id: string; name: string }>>([])
    const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(initialAccount)
    const [isSearching, setIsSearching] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)

    const defaultValues = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        contactCategoryId: contactCategories.length > 0 ? contactCategories[0].id : "",
        accountId: "",
    }

    useEffect(() => {
        if (!isModal && accountSearch.length >= 2) {
            setIsSearching(true)
            const timeoutId = setTimeout(async () => {
                try {
                    const url = apiWorkspaceAccounts.index.path({ venue_slug: venueSlug })
                    const response = await fetch(`${url}?q[name_cont]=${encodeURIComponent(accountSearch)}`)
                    const data = await response.json()
                    setAccountResults(data.accounts || [])
                    setShowDropdown(true)
                } catch (error) {
                    console.error("Error searching accounts:", error)
                } finally {
                    setIsSearching(false)
                }
            }, 300)

            return () => clearTimeout(timeoutId)
        } else {
            setAccountResults([])
            setShowDropdown(false)
        }
    }, [accountSearch, venueSlug, isModal])

    const handleFormSubmit = (values: any) => {
        const formData = {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            phone: values.phone,
            role: values.role,
            contact_category_id: values.contactCategoryId,
            account_id: selectedAccount?.id || null,
        }
        onSubmit(formData)
    }

    const contactCategoryOptions = contactCategories.map((category) => ({
        value: category.id,
        label: category.name,
    }))

    const FormFields = () => (
        <>
            <div className="row">
                <div className="col-6">
                    <FormikInput
                        name="firstName"
                        label="First Name"
                        placeholder="John"
                        required
                        helpText=""
                    />
                </div>
                <div className="col-6">
                    <FormikInput
                        name="lastName"
                        label="Last Name"
                        placeholder="Doe"
                        required
                        helpText=""
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-6">
                    <FormikInput
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="john.doe@example.com"
                        required
                        helpText=""
                    />
                </div>
                <div className="col-6">
                    <FormikInput
                        name="phone"
                        label="Phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        helpText=""
                    />
                </div>
            </div>

            <FormikInput
                name="role"
                label="Role"
                placeholder="Marketing Manager"
                helpText=""
            />

            <FormikSelect
                name="contactCategoryId"
                label="Category"
                options={contactCategoryOptions}
                required
                helpText=""
                noEmptyOption={true}
            />

            {!isModal && (
                <div className="mb-3">
                    <label className="form-label">
                        Account
                    </label>
                    <div className="position-relative">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search for an account..."
                            value={selectedAccount ? selectedAccount.name : accountSearch}
                            onChange={(e) => {
                                setAccountSearch(e.target.value)
                                if (selectedAccount) {
                                    setSelectedAccount(null)
                                }
                            }}
                            onFocus={() => {
                                if (accountResults.length > 0) {
                                    setShowDropdown(true)
                                }
                            }}
                            disabled={isSubmitting}
                        />
                        {isSearching && (
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                <span className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </span>
                            </div>
                        )}
                        {showDropdown && accountResults.length > 0 && (
                            <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {accountResults.map((account) => (
                                    <button
                                        key={account.id}
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                            setSelectedAccount(account)
                                            setAccountSearch("")
                                            setShowDropdown(false)
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        {account.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {selectedAccount && (
                            <div className="mt-2">
                                <span className="badge bg-primary me-2">{selectedAccount.name}</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-danger p-0"
                                    onClick={() => {
                                        setSelectedAccount(null)
                                        setAccountSearch("")
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="form-text">Optional: Associate this contact with an account</div>
                </div>
            )}
        </>
    )

    return (
        <FormikWrapper
            onSubmit={handleFormSubmit}
            validationSchema={contactSchema}
            initialValues={isEditing ? initialValues : defaultValues}
        >
            {isModal ? (
                <>
                    <FormFields />

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </span>
                                    Creating...
                                </>
                            ) : (
                                isEditing ? "Update Contact" : "Create Contact"
                            )}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <CatchCard title="Contact Information">
                        <FormFields />
                    </CatchCard>

                    <div className="d-flex">
                        <div className="ms-auto">
                            <Link
                                className="btn btn-secondary me-2"
                                to="/contacts"
                                onClick={(e) => {
                                    if (isSubmitting) {
                                        e.preventDefault()
                                    }
                                }}
                                style={{
                                    pointerEvents: isSubmitting ? 'none' : 'auto',
                                    opacity: isSubmitting ? 0.6 : 1
                                }}
                            >
                                Cancel
                            </Link>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </span>
                                        {isEditing ? "Updating..." : "Creating..."}
                                    </>
                                ) : (
                                    isEditing ? "Update Contact" : "Create Contact"
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </FormikWrapper>
    )
}

export default ContactForm